/**
 * PublicAppLayout — Layout for unauthenticated visitors browsing public pages
 * (rodeos, jackpots, event details).
 *
 * Header-only chrome (no sidebar). Header has:
 *   - HighCall brand
 *   - Top-level "Rodeos" / "Jackpots" nav
 *   - Theme toggle
 *   - Sign In / Sign Up CTAs (instead of UserMenu)
 *
 * For authenticated users browsing the same routes, ResponsiveLayout swaps
 * this for the full AppLayout. See App.tsx routing.
 */

import { Outlet, Link, NavLink } from 'react-router-dom';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../ThemeProvider';
import { BreadcrumbProvider } from '../../contexts/BreadcrumbContext';
import { cn } from '../../lib/utils';

export function PublicAppLayout() {
  const { mode, setMode, actualMode } = useTheme();

  const cycleTheme = () => {
    const next = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
    setMode(next);
  };

  const ThemeIcon = mode === 'system' ? Monitor : actualMode === 'dark' ? Moon : Sun;

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'text-sm font-medium tracking-wide transition-opacity',
      isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
    );

  return (
    <BreadcrumbProvider>
      <div className="min-h-screen bg-background">
        <header
          className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-border px-4 md:px-6"
          style={{
            backgroundColor: 'hsl(var(--header-background))',
            color: 'hsl(var(--header-foreground))',
          }}
        >
          {/* Brand — links to public landing page */}
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0"
          >
            <img
              src="/icons/HighCallRodeo-icon-white.png"
              alt=""
              className="h-[25px] w-auto"
            />
            <span className="text-sm font-semibold tracking-[0.15em] uppercase hidden sm:inline">
              HighCall Rodeo
            </span>
          </Link>

          {/* Top-level browse nav */}
          <nav className="flex items-center gap-5 ml-2" aria-label="Primary">
            <NavLink to="/rodeos" className={navLinkClass}>
              Rodeos
            </NavLink>
            <NavLink to="/jackpots" className={navLinkClass}>
              Jackpots
            </NavLink>
          </nav>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side */}
          <div className="flex items-center gap-2">
            <button
              onClick={cycleTheme}
              className="inline-flex items-center justify-center rounded-md p-2 opacity-60 hover:opacity-100 transition-opacity"
              aria-label={`Theme: ${mode}`}
              title={mode === 'system' ? `System (${actualMode})` : mode}
            >
              <ThemeIcon className="h-4 w-4" />
            </button>

            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium opacity-80 hover:opacity-100 transition-opacity"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Sign Up
            </Link>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </BreadcrumbProvider>
  );
}
